"use client";

import { useEffect, useState } from "react";
import { CalendarCheck2, X } from "lucide-react";
import { usePathname } from "next/navigation";
import { claimAuthorChallengePromotion } from "@/lib/actions/promotion-notification-actions";
import { AuthorChallengeEnrollmentForm } from "@/components/promotions/author-challenge-enrollment-form";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/providers/auth-provider";
import { AUTHOR_PROMOTION_ENABLED } from "@/lib/promotions/author-challenge-progress";
export const AUTHOR_CHALLENGE_DISMISSED_KEY = "iloris-author-challenge-2026-dismissed";
export function AuthorChallengeBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const pathname = usePathname();
  const {
    loading,
    profile,
    user
  } = useAuth();
  const userId = user?.id;
  const isPasswordRecoveryPage = pathname === "/account/reset-password";
  useEffect(() => {
    if (!AUTHOR_PROMOTION_ENABLED || isPasswordRecoveryPage) {
      return;
    }
    function handleStorage(event: StorageEvent) {
      if (event.key === AUTHOR_CHALLENGE_DISMISSED_KEY && event.newValue === "1") {
        setIsVisible(false);
      }
    }
    window.addEventListener("storage", handleStorage);
    let isCancelled = false;
    const timer = window.setTimeout(async () => {
      if (loading || !userId || !profile?.canAuthor) {
        if (!isCancelled) setIsVisible(false);
        return;
      }
      let wasLocallyDismissed = false;
      try {
        wasLocallyDismissed = window.localStorage.getItem(AUTHOR_CHALLENGE_DISMISSED_KEY) === "1";
      } catch {
        // The durable notification state remains authoritative.
      }
      try {
        const {
          shouldShow
        } = await claimAuthorChallengePromotion();
        if (!isCancelled) {
          setIsVisible(shouldShow && !wasLocallyDismissed);
        }
      } catch {
        // Fail closed: never show an author a promotion whose durable
        // seen state could not be checked.
        if (!isCancelled) setIsVisible(false);
      }
    }, 0);
    return () => {
      isCancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener("storage", handleStorage);
    };
  }, [isPasswordRecoveryPage, loading, profile?.canAuthor, userId]);
  function dismissBanner() {
    try {
      window.localStorage.setItem(AUTHOR_CHALLENGE_DISMISSED_KEY, "1");
    } catch {
      // The banner still stays dismissed for this page view.
    }
    setIsVisible(false);
  }
  if (!AUTHOR_PROMOTION_ENABLED || isPasswordRecoveryPage || !isVisible) return null;
  return <aside aria-label="Author promotion" className="fixed inset-x-4 top-20 bottom-auto z-40 overflow-hidden rounded-2xl border border-brand/25 bg-card shadow-2xl shadow-black/15 sm:inset-x-auto sm:right-5 sm:w-[22rem]">
            <div className="h-1 bg-gradient-to-r from-brand via-primary to-brand" />
            <button type="button" onClick={dismissBanner} aria-label="Dismiss promotion" className="absolute right-3 top-4 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <X className="size-4" />
            </button>

            <div className="space-y-4 p-5 pr-11">
                <div className="flex gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand">
                        <CalendarCheck2 className="size-5" />
                    </div>
                    <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-brand">
                            Author challenge
                        </p>
                        <h2 className="mt-1 font-heading text-lg font-bold leading-tight text-foreground">
                            Post 1 chapter a week, earn $30
                        </h2>
                    </div>
                </div>

                <p className="text-sm leading-relaxed text-muted-foreground">
                    Publish consistently on one story for 3 months. No contest,
                    no judging, no competing.
                </p>

                <Dialog>
                    <DialogTrigger asChild>
                        <Button className="w-full">View details &amp; join</Button>
                    </DialogTrigger>
                    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="pr-8 font-heading text-2xl leading-tight">
                                Post 1 chapter a week for 12 weeks, earn $30
                            </DialogTitle>
                            <DialogDescription className="text-base leading-relaxed">
                                Build a publishing rhythm on one Iloris story. If
                                you complete all 12 weeks, we&apos;ll pay you
                                $30 USD.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-5 text-sm leading-relaxed text-foreground">
                            <section>
                                <h3 className="mb-2 font-semibold">How it works</h3>
                                <ul className="list-disc space-y-1.5 pl-5 text-muted-foreground">
                                    <li>Choose one story; it is locked in once enrolled.</li>
                                    <li>
                                        Post at least 1 chapter in each of 12
                                        consecutive 7-day windows.
                                    </li>
                                    <li>
                                        A chapter may be published early, but it must
                                        reach 1,000 words before that week closes.
                                    </li>
                                    <li>
                                        It counts in the week it first reaches 1,000
                                        words while the story is public.
                                    </li>
                                    <li>
                                        Qualifying chapters must remain published
                                        until payout review.
                                    </li>
                                    <li>
                                        Miss a week and you will no longer qualify
                                        for this challenge.
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h3 className="mb-2 font-semibold">Getting paid</h3>
                                <p className="text-muted-foreground">
                                    After Week 12, we&apos;ll review the
                                    chapters and send $30 USD by PayPal within 3–5
                                    business days. You must be 18 or older, have a
                                    PayPal account, and handle any taxes owed.
                                </p>
                            </section>

                            <section>
                                <h3 className="mb-2 font-semibold">Eligibility</h3>
                                <p className="text-muted-foreground">
                                    One payout per author. Chapters must be genuine
                                    story content; filler, duplicates, placeholders,
                                    or content removed for guideline violations do
                                    not count. Iloris makes the final eligibility
                                    decision and may change or end the program with
                                    notice.
                                </p>
                            </section>

                            <div className="border-t border-border pt-5">
                                <h3 className="mb-3 font-semibold">
                                    Enroll your story
                                </h3>
                                <AuthorChallengeEnrollmentForm />
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </aside>;
}